/**
 * 可见范围选择弹窗：组织架构（懒加载）/ 角色 / 用户
 */
import * as React from 'react';
import { toast } from '@heroui/react';
import {
  Avatar,
  Button,
  Checkbox,
  Description,
  Input,
  Modal,
  Tabs,
} from '@heroui/react';
import {
  IconSearch as Search,
  IconChevronDown as ChevronDown,
  IconChevronRight as ChevronRight,
  IconX as X,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';
import { selectionKey, type VisibilityTargetType } from './visibility-utils';

export type { VisibilityTargetType };

export type VisibilitySelection = {
  key: string;
  targetType: VisibilityTargetType;
  targetId: string;
  label: string;
  avatarUrl?: string | null;
};

export interface AddVisibilityPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appId: string;
  spaceId: string;
  /** 已有规则，用于去重 */
  existingKeys?: Set<string>;
  onSuccess?: () => void;
}

type OrgNode = {
  id: string;
  label: string;
  parentId?: string | null;
};

type PersonNode = {
  id: string;
  label: string;
  avatarUrl?: string | null;
  userId?: string | null;
};

export function AddVisibilityPickerModal({
  open,
  onOpenChange,
  appId,
  spaceId,
  existingKeys = new Set(),
  onSuccess,
}: AddVisibilityPickerModalProps) {
  const [keyword, setKeyword] = React.useState('');
  const [tab, setTab] = React.useState<'org' | 'role' | 'user'>('org');
  const [expandedOrgIds, setExpandedOrgIds] = React.useState<Set<string>>(new Set());
  const [orgChildrenCache, setOrgChildrenCache] = React.useState<
    Map<string, { orgs: OrgNode[]; people: PersonNode[] }>
  >(new Map());
  const [selected, setSelected] = React.useState<VisibilitySelection[]>([]);
  const [loadingOrgId, setLoadingOrgId] = React.useState<string | null>(null);

  const utils = trpc.useUtils();

  const trimmedKeyword = keyword.trim() || undefined;

  const { data: rootData, isLoading: rootLoading } = trpc.contacts.getVisibilityPickerRoot.useQuery(
    { keyword: trimmedKeyword },
    { enabled: open && tab === 'org' }
  );

  const { data: rolesList, isLoading: rolesLoading } = trpc.spaceRoles.listAll.useQuery(
    {},
    { enabled: open && tab === 'role' }
  );

  const { data: membersPage, isLoading: membersLoading } = trpc.spaceMembers.list.useQuery(
    {
      page: 1,
      pageSize: 50,
      keyword: trimmedKeyword,
      sortBy: 'createdAt',
      sortOrder: 'asc',
    },
    { enabled: open && tab === 'user' }
  );

  const createManyMutation = trpc.spaceAppVisibility.createMany.useMutation({
    onSuccess: (created) => {
      toast.success(`已添加 ${created.length} 条可见性规则`);
      handleClose();
      onSuccess?.();
    },
    onError: (e) => toast.danger(e.message || '添加失败'),
  });

  const resetState = React.useCallback(() => {
    setKeyword('');
    setTab('org');
    setExpandedOrgIds(new Set());
    setOrgChildrenCache(new Map());
    setSelected([]);
    setLoadingOrgId(null);
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [onOpenChange, resetState]);

  const handleModalOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) handleClose();
      else onOpenChange(true);
    },
    [handleClose, onOpenChange]
  );

  const selectedKeys = React.useMemo(() => new Set(selected.map((s) => s.key)), [selected]);

  const toggleSelection = React.useCallback(
    (item: VisibilitySelection) => {
      if (existingKeys.has(item.key)) return;
      setSelected((prev) => {
        const has = prev.some((p) => p.key === item.key);
        if (has) return prev.filter((p) => p.key !== item.key);
        return [...prev, item];
      });
    },
    [existingKeys]
  );

  const loadOrgChildren = React.useCallback(
    async (orgId: string) => {
      if (orgChildrenCache.has(orgId)) return;
      setLoadingOrgId(orgId);
      try {
        const data = await utils.contacts.getVisibilityPickerOrgChildren.fetch({
          id: orgId,
          keyword: trimmedKeyword,
        });
        const orgs: OrgNode[] = (data.orgs ?? [])
          .filter((o): o is typeof o & { id: string } => o.id != null)
          .map((o) => ({
            id: o.id,
            label: o.shortName || o.fullName || o.code || o.id,
            parentId: o.parentId ?? null,
          }));
        const people: PersonNode[] = (data.people ?? [])
          .filter((p): p is typeof p & { id: string } => p.id != null)
          .map((p) => ({
            id: p.id,
            label: p.fullName || p.jobNumber || p.id,
            avatarUrl: p.avatarUrl ?? null,
            userId: p.user?.id ?? null,
          }));
        setOrgChildrenCache((prev) => new Map(prev).set(orgId, { orgs, people }));
      } catch (e) {
        toast.danger(e instanceof Error ? e.message : '加载下级组织失败');
      } finally {
        setLoadingOrgId(null);
      }
    },
    [orgChildrenCache, trimmedKeyword, utils.contacts.getVisibilityPickerOrgChildren]
  );

  const toggleOrgExpand = React.useCallback(
    async (orgId: string) => {
      const nextExpanded = new Set(expandedOrgIds);
      if (nextExpanded.has(orgId)) {
        nextExpanded.delete(orgId);
        setExpandedOrgIds(nextExpanded);
        return;
      }
      await loadOrgChildren(orgId);
      nextExpanded.add(orgId);
      setExpandedOrgIds(nextExpanded);
    },
    [expandedOrgIds, loadOrgChildren]
  );

  const rootOrgs = React.useMemo(
    () =>
      (rootData?.orgs ?? [])
        .filter((o): o is typeof o & { id: string } => o.id != null)
        .map((o) => ({
          id: o.id,
          label: o.shortName || o.fullName || o.code || o.id,
          parentId: o.parentId ?? null,
        })),
    [rootData?.orgs]
  );

  const rootPeople = React.useMemo(
    () =>
      (rootData?.people ?? [])
        .filter((p): p is typeof p & { id: string } => p.id != null)
        .map((p) => ({
          id: p.id,
          label: p.fullName || p.jobNumber || p.id,
          avatarUrl: p.avatarUrl ?? null,
          userId: p.user?.id ?? null,
        })),
    [rootData?.people]
  );

  const renderOrgTree = React.useCallback(
    (orgs: OrgNode[], people: PersonNode[], depth: number) => {
      const pad = depth * 16;
      return (
        <>
          {orgs.map((org) => {
            const expanded = expandedOrgIds.has(org.id);
            const loading = loadingOrgId === org.id;
            const cached = orgChildrenCache.get(org.id);
            const orgKey = selectionKey('ORG', org.id);
            const orgSelected = selectedKeys.has(orgKey);
            const orgDisabled = existingKeys.has(orgKey);

            return (
              <React.Fragment key={`org-${org.id}`}>
                <div
                  className="hover:bg-accent/10 flex items-center gap-2 rounded-md py-1 pr-2"
                  style={{ paddingLeft: pad }}
                >
                  <button
                    type="button"
                    className="text-muted-foreground inline-flex size-6 shrink-0 items-center justify-center rounded"
                    aria-label={expanded ? '收起' : '展开'}
                    onClick={() => void toggleOrgExpand(org.id)}
                  >
                    {loading ? (
                      <span className="text-xs">…</span>
                    ) : expanded ? (
                      <ChevronDown className="size-4" />
                    ) : (
                      <ChevronRight className="size-4" />
                    )}
                  </button>
                  <Checkbox
                    isSelected={orgSelected}
                    isDisabled={orgDisabled}
                    onChange={() =>
                      toggleSelection({
                        key: orgKey,
                        targetType: 'ORG',
                        targetId: org.id,
                        label: org.label,
                      })
                    }
                  >
                    <Checkbox.Control>
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox>
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate text-left text-sm"
                    onClick={() => void toggleOrgExpand(org.id)}
                  >
                    {org.label}
                  </button>
                </div>
                {expanded && cached
                  ? renderOrgTree(cached.orgs, cached.people, depth + 1)
                  : null}
              </React.Fragment>
            );
          })}
          {people.map((person) => {
            const peopleKey = selectionKey('PEOPLE', person.id);
            const userKey = person.userId ? selectionKey('USER', person.userId) : null;
            const key = userKey && existingKeys.has(userKey) ? userKey : peopleKey;
            const targetType: VisibilityTargetType = userKey && !existingKeys.has(peopleKey) && person.userId ? 'USER' : 'PEOPLE';
            const targetId = targetType === 'USER' && person.userId ? person.userId : person.id;
            const finalKey = selectionKey(targetType, targetId);
            const checked = selectedKeys.has(finalKey);
            const disabled = existingKeys.has(finalKey);

            return (
              <label
                key={`person-${person.id}`}
                className="hover:bg-accent/10 flex cursor-pointer items-center gap-3 rounded-md py-1 pr-2"
                style={{ paddingLeft: pad + 24 }}
              >
                <Checkbox
                  isSelected={checked}
                  isDisabled={disabled}
                  onChange={() =>
                    toggleSelection({
                      key: finalKey,
                      targetType,
                      targetId,
                      label: person.label,
                      avatarUrl: person.avatarUrl,
                    })
                  }
                >
                  <Checkbox.Control>
                    <Checkbox.Indicator />
                  </Checkbox.Control>
                </Checkbox>
                <Avatar className="size-7 shrink-0" variant="soft">
                  {person.avatarUrl ? (
                    <Avatar.Image src={person.avatarUrl} alt={person.label} />
                  ) : null}
                  <Avatar.Fallback>{person.label.charAt(0).toUpperCase()}</Avatar.Fallback>
                </Avatar>
                <span className="truncate text-sm">{person.label}</span>
              </label>
            );
          })}
        </>
      );
    },
    [
      existingKeys,
      expandedOrgIds,
      loadingOrgId,
      orgChildrenCache,
      selectedKeys,
      toggleOrgExpand,
      toggleSelection,
    ]
  );

  const handleSubmit = React.useCallback(() => {
    if (selected.length === 0) return;
    const payload = selected.map((s) => ({
      spaceId,
      appId,
      targetType: s.targetType,
      targetId: s.targetId,
    }));
    createManyMutation.mutate(payload);
  }, [selected, spaceId, appId, createManyMutation]);

  const roleItems = React.useMemo(
    () =>
      (rolesList ?? [])
        .filter((r): r is typeof r & { id: string } => r.id != null)
        .map((r) => ({
          id: r.id,
          label: r.name || r.code || r.id,
        })),
    [rolesList]
  );

  const memberItems = React.useMemo(
    () =>
      (membersPage?.items ?? []).map((m) => {
        const uid = m.userId ?? m.user?.id ?? '';
        const label = m.user?.nickname || m.user?.username || `用户 ${uid}`;
        return {
          id: uid,
          label,
          avatarUrl: m.user?.avatarUrl ?? null,
        };
      }).filter((m) => m.id),
    [membersPage?.items]
  );

  return (
    <Modal isOpen={open} onOpenChange={handleModalOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden">
            <Modal.Header className="shrink-0">
              <Modal.Heading className="text-xl font-medium">可见范围</Modal.Heading>
              <Description className="sr-only">选择组织、角色或用户作为应用可见范围</Description>
            </Modal.Header>

            <Modal.Body className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
              <div className="relative shrink-0">
                <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" />
                <Input
                  className="pl-10"
                  placeholder="搜索组织、人员、角色、用户"
                  value={keyword}
                  aria-label="搜索"
                  onChange={(e) => setKeyword(e.target.value)}
                />
              </div>

              <Tabs
                selectedKey={tab}
                onSelectionChange={(k) => setTab(k as 'org' | 'role' | 'user')}
                className="flex min-h-0 flex-1 flex-col"
              >
                <Tabs.List className="shrink-0">
                  <Tabs.Tab id="org">组织架构</Tabs.Tab>
                  <Tabs.Tab id="role">角色</Tabs.Tab>
                  <Tabs.Tab id="user">用户</Tabs.Tab>
                </Tabs.List>

                <div className="flex min-h-[360px] flex-1 flex-row overflow-hidden rounded-3xl bg-surface-secondary">
                  <div className="border-border flex w-3/5 min-w-0 flex-col overflow-hidden border-r">
                    <Tabs.Panel id="org" className="flex min-h-0 flex-1 flex-col overflow-hidden p-2">
                      <div className="min-h-0 flex-1 space-y-0.5 overflow-y-auto">
                        {rootLoading ? (
                          <p className="text-muted-foreground px-2 py-4 text-sm">加载中…</p>
                        ) : (
                          renderOrgTree(rootOrgs, rootPeople, 0)
                        )}
                      </div>
                    </Tabs.Panel>

                    <Tabs.Panel id="role" className="min-h-0 flex-1 overflow-y-auto p-2">
                      {rolesLoading ? (
                        <p className="text-muted-foreground px-2 py-4 text-sm">加载中…</p>
                      ) : (
                        roleItems.map((role) => {
                          const key = selectionKey('ROLE', role.id);
                          return (
                            <label
                              key={role.id}
                              className="hover:bg-accent/10 flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5"
                            >
                              <Checkbox
                                isSelected={selectedKeys.has(key)}
                                isDisabled={existingKeys.has(key)}
                                onChange={() =>
                                  toggleSelection({
                                    key,
                                    targetType: 'ROLE',
                                    targetId: role.id,
                                    label: role.label,
                                  })
                                }
                              >
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                              </Checkbox>
                              <span className="text-sm">{role.label}</span>
                            </label>
                          );
                        })
                      )}
                    </Tabs.Panel>

                    <Tabs.Panel id="user" className="min-h-0 flex-1 overflow-y-auto p-2">
                      {membersLoading ? (
                        <p className="text-muted-foreground px-2 py-4 text-sm">加载中…</p>
                      ) : (
                        memberItems.map((member) => {
                          const key = selectionKey('USER', member.id);
                          return (
                            <label
                              key={member.id}
                              className="hover:bg-accent/10 flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5"
                            >
                              <Checkbox
                                isSelected={selectedKeys.has(key)}
                                isDisabled={existingKeys.has(key)}
                                onChange={() =>
                                  toggleSelection({
                                    key,
                                    targetType: 'USER',
                                    targetId: member.id,
                                    label: member.label,
                                    avatarUrl: member.avatarUrl,
                                  })
                                }
                              >
                                <Checkbox.Control>
                                  <Checkbox.Indicator />
                                </Checkbox.Control>
                              </Checkbox>
                              <Avatar className="size-7 shrink-0" variant="soft">
                                {member.avatarUrl ? (
                                  <Avatar.Image src={member.avatarUrl} alt={member.label} />
                                ) : null}
                                <Avatar.Fallback>{member.label.charAt(0).toUpperCase()}</Avatar.Fallback>
                              </Avatar>
                              <span className="text-sm">{member.label}</span>
                            </label>
                          );
                        })
                      )}
                    </Tabs.Panel>
                  </div>

                  <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    <div className="border-border flex h-12 shrink-0 items-center border-b px-4">
                      <h4 className="text-sm font-medium">已选择</h4>
                    </div>
                    <div className="flex h-14 shrink-0 items-center px-4">
                      <p className="text-muted-foreground text-sm">{selected.length} 项</p>
                    </div>
                    <div className="flex-1 space-y-1 overflow-y-auto px-4 py-2">
                      {selected.map((s) => (
                        <div
                          key={s.key}
                          className="bg-accent/10 flex items-center gap-2 rounded-md px-2 py-1.5"
                        >
                          <Avatar className="size-7 shrink-0" variant="soft">
                            {s.avatarUrl ? <Avatar.Image src={s.avatarUrl} alt={s.label} /> : null}
                            <Avatar.Fallback>{s.label.charAt(0).toUpperCase()}</Avatar.Fallback>
                          </Avatar>
                          <div className="min-w-0 flex-1 truncate text-sm">{s.label}</div>
                          <Button
                            type="button"
                            isIconOnly
                            size="sm"
                            variant="ghost"
                            className="shrink-0"
                            onPress={() =>
                              setSelected((prev) => prev.filter((p) => p.key !== s.key))
                            }
                            aria-label="移除"
                          >
                            <X className="size-3.5" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Tabs>
            </Modal.Body>

            <Modal.Footer>
              <Button type="button" variant="ghost" onPress={handleClose}>
                取消
              </Button>
              <Button
                variant="primary"
                onPress={handleSubmit}
                isDisabled={createManyMutation.isPending || selected.length === 0}
              >
                {createManyMutation.isPending ? '提交中…' : '确定'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
